import { useMemo } from "react";
import type { Tier } from "@colab/shared";
import { usePrograms } from "@/features/admin/tiers/use-programs";
import { useProgram } from "@/features/admin/tiers/use-program";

// Resolves the default tier program's tiers so admin surfaces (e.g. the
// partners list) can render a partner's tier by its assignment (`tier_id`)
// rather than the legacy enum. Tier display is assignment-driven: a partner
// with no default-program assignment has `tier_id = null` and renders empty.
// Reuses the cached usePrograms/useProgram queries — no extra round-trips —
// and returns an empty map when there's no default program or the viewer
// lacks tiers.read.
export function useDefaultProgramTierMap(): {
  /** tier id → default-program tier. */
  tierById: Map<string, Tier>;
  /** default-program tiers ordered low → high by position. */
  orderedTiers: Tier[];
} {
  const programsQuery = usePrograms();
  const defaultProgram = useMemo(
    () => (programsQuery.data ?? []).find((p) => p.is_default) ?? null,
    [programsQuery.data],
  );
  const programDetail = useProgram(defaultProgram?.id);

  return useMemo(() => {
    const orderedTiers = [...(programDetail.data?.tiers ?? [])].sort(
      (a, b) => a.position - b.position,
    );
    const tierById = new Map<string, Tier>(orderedTiers.map((t) => [t.id, t]));
    return { tierById, orderedTiers };
  }, [programDetail.data]);
}
